import React, { useState, useMemo } from "react"
import PropTypes from "prop-types"
import { useOutletContext } from "react-router-dom"
import { Space, Checkbox, Typography, Button } from "antd"
import keyBy from "lodash.keyby"

import { W0 } from "@components"
import { Header } from "@components/Form"
import { useAppContext } from "@components/AppContext"
import { MarkdownAlert } from "@components/Text"

const LABEL_SAVE = "Save Changes"
const LABEL_APPROVERS = "Restricted Distribution Approvers"
const LABEL_APPROVERS_TEXT = `The following transaction approvers are authorized
to approve distributions using this bank account. If no users are selected, any
approver will be eligible to approve distributions.`
const LABEL_NOT_CONTROLLER = `Only the bank account controller, **[NAME]**, can
modify this list.`
const LABEL_NO_APPROVERS_TEXT = `There are no users with the **Transaction
Approver** role.`


const TransactionApproversList = ({ bankAccountId }) => {
  const { isSourceController } = useAppContext()
  const {
    activeApprovers,
    bankAccountsMap,
    computeUserFullname,
    updateBankAccountApprovers,
    getControllerNameForBankAccount,
  } = useOutletContext()

  const bankAccount = bankAccountsMap[bankAccountId]

  const {
    sourceId,
    approverUserIds,
  } = bankAccount

  const initialApproverUserIds = useMemo(() => {
    const activeApproversMap = keyBy(activeApprovers, 'id')

    return approverUserIds.filter(id => !!activeApproversMap[id])
  }, [
    activeApprovers,
    approverUserIds
  ])

  const [ isLoading, setIsLoading ] = useState(false)
  const [ newApproverUserIds, setNewApproverUserIds ] = useState(initialApproverUserIds)

  const isController = isSourceController(sourceId)

  const options = activeApprovers
    .map(({ id }) => ({
      value: id,
      label: computeUserFullname(id),
      disabled: !isController,
    }))

  const onChange = (values) => setNewApproverUserIds(values)

  const isDisabled =
    initialApproverUserIds.sort().join('-') === newApproverUserIds.sort().join('-')

  const hasOptions = options.length > 0

  if (!hasOptions) {
    return (
      <Space size={W0} direction="vertical" style={{ width: "100%" }}>
        <Header style={{ marginBottom: 0 }}>
          {LABEL_APPROVERS}
        </Header>

        <MarkdownAlert
          type="warning"
          text={LABEL_NO_APPROVERS_TEXT}
        />
      </Space>
    )
  }

  const renderFooter = () => {
    if (isController) {
      const onClick = () => {
        setIsLoading(true)

        return updateBankAccountApprovers(bankAccountId, newApproverUserIds)
          .then(() => setIsLoading(false))
      }

      return (
        <Button
          size="small"
          onClick={onClick}
          loading={isLoading}
          disabled={isDisabled}
        >
          {LABEL_SAVE}
        </Button>
      )
    }

    const controllerName = getControllerNameForBankAccount(bankAccountId)
    const text = LABEL_NOT_CONTROLLER.replace('[NAME]', controllerName)

    return (
      <MarkdownAlert
        type="warning"
        text={text}
        style={{ marginBottom: 0 }}
      />
    )
  }

  return (
    <Space size={W0} direction="vertical" style={{ width: "100%" }}>
      <div>
        <Header style={{ marginBottom: W0 / 2 }}>
          {LABEL_APPROVERS}
        </Header>

        <Typography.Text type="secondary">
          {LABEL_APPROVERS_TEXT}
        </Typography.Text>
      </div>

      <Checkbox.Group
        onChange={onChange}
        defaultValue={initialApproverUserIds}
      >
        {
          options.map(({ value, label, disabled }) =>
            <div key={value} style={{ width: "100%", padding: "1px 0" }}>
              <Checkbox value={value} disabled={disabled}>
                {label}
              </Checkbox>
            </div>
          )
        }
      </Checkbox.Group>

      {renderFooter()}
    </Space>
  )
}

TransactionApproversList.propTypes = {
  bankAccountId: PropTypes.string.isRequired,
}

export default TransactionApproversList
